import { useEffect, useRef } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import { X, Check, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import type { LearnStep, StepStatus } from '@/pages/learner/ui-model/types'

export interface LearningSidebarProps {
  title?: string
  steps: LearnStep[]
  selectedStepId?: string
  onSelect?: (step: LearnStep) => void
  className?: string
  variant?: 'inline' | 'drawer'
  open?: boolean
  onClose?: () => void
  /** True renders the inline variant as a 64px rail. Drawer variant ignores this. */
  collapsed?: boolean
  /** When set on the inline variant, a header toggle button is rendered. */
  onCollapseToggle?: () => void
}

const circleByStatus: Record<StepStatus, string> = {
  done: 'bg-lc-success-500 text-lc-text-inverse',
  current: 'bg-lc-key-500 text-lc-text-inverse',
  locked: 'bg-lc-v3-gray-300 text-lc-text-tertiary',
}
const labelByStatus: Record<StepStatus, string> = {
  done: 'font-medium text-lc-text-secondary',
  current: 'font-semibold text-lc-text-link',
  locked: 'font-semibold text-lc-text-disabled',
}

function StepList({
  steps,
  selectedStepId,
  onSelect,
  title,
  collapsed = false,
}: {
  steps: LearnStep[]
  selectedStepId?: string
  onSelect?: (step: LearnStep) => void
  title: string
  collapsed?: boolean
}) {
  return (
    <>
      {title && !collapsed ? (
        <div className="mb-2 text-lc-v3-body-2 font-bold text-lc-text-tertiary">
          {title}
        </div>
      ) : null}
      <ul className="flex w-full flex-col">
        {steps.map((step) => {
          const clickable = step.status !== 'locked' && !!onSelect
          const isSelected = step.id === selectedStepId
          const isActive = selectedStepId === undefined
            ? step.status === 'current'
            : isSelected
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={clickable ? () => onSelect!(step) : undefined}
                disabled={!clickable}
                aria-current={isActive ? 'step' : undefined}
                aria-label={step.label}
                title={collapsed ? step.label : undefined}
                className={cn(
                  'flex min-h-[52px] w-full items-center text-left',
                  'focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-lc-key-500',
                  isActive && 'border-r border-lc-v3-gray-300 bg-lc-key-50',
                  clickable && !isActive && 'hover:bg-lc-v3-gray-50',
                  collapsed
                    ? 'justify-center px-2 py-2.5'
                    : 'gap-2 pl-4 pr-2 py-2.5',
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] text-lc-v3-body-1 font-bold',
                    circleByStatus[step.status],
                  )}
                >
                  {step.status === 'done' ? <Check className="size-5" strokeWidth={2.5} aria-hidden /> : step.circleLabel}
                </span>
                {!collapsed ? (
                  <span className={cn('min-w-0 text-lc-v3-body-2', labelByStatus[step.status])}>
                    <ContentMetadataText text={step.label} />
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>
    </>
  )
}

export function LearningSidebar({
  title,
  steps,
  selectedStepId,
  onSelect,
  className,
  variant = 'inline',
  open = false,
  onClose,
  collapsed = false,
  onCollapseToggle,
}: LearningSidebarProps) {
  const { t } = useTranslation('learner')
  const resolvedTitle = title ?? t('learn.sidebar.title')
  const drawerRef = useRef<HTMLElement | null>(null)
  useFocusTrap(drawerRef, variant === 'drawer' && open)

  useEffect(() => {
    if (variant !== 'drawer' || !open || !onClose) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose!()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [variant, open, onClose])

  if (variant === 'drawer') {
    return (
      <div
        className={cn(
          'fixed inset-0 z-40 transition-opacity',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
          className,
        )}
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <aside
          ref={drawerRef}
          className={cn(
            'absolute inset-y-0 left-0 w-[280px] bg-white px-4 py-5 shadow-lc-soft overflow-y-auto',
            'transform transition-transform duration-200 ease-out',
            open ? 'translate-x-0' : '-translate-x-full',
          )}
          role="dialog"
          aria-modal="true"
          aria-label={t('aria.stepList')}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="text-lc-v3-body-2 font-bold text-lc-text-tertiary">
              {resolvedTitle}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('aria.close')}
              className="w-8 h-8 rounded-full inline-flex items-center justify-center hover:bg-lc-gray-100 text-lc-gray-600"
            >
              <X className="size-5" />
            </button>
          </div>
          <StepList title="" steps={steps} selectedStepId={selectedStepId} onSelect={onSelect} />
        </aside>
      </div>
    )
  }

  const toggleAriaKey = collapsed ? 'aria.sidebarToggleExpand' : 'aria.sidebarToggleCollapse'
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col items-start gap-5 overflow-y-auto border-r border-[#e5e5ea] bg-lc-bg-primary py-4 motion-reduce:transition-none',
        'transition-[width] duration-200 ease-[cubic-bezier(.22,.8,.36,1)]',
        collapsed ? 'w-16' : 'w-[240px]',
        className,
      )}
    >
      {onCollapseToggle && (
        <div
          className={cn(
            'flex h-10 w-full shrink-0 items-center px-4',
            collapsed ? 'justify-center' : 'justify-between',
          )}
        >
          {!collapsed && (
            <div className="text-lc-v3-body-2 font-bold text-lc-text-tertiary">
              {resolvedTitle}
            </div>
          )}
          <button
            type="button"
            onClick={onCollapseToggle}
            aria-label={t(toggleAriaKey)}
            aria-expanded={!collapsed}
            className="inline-flex size-10 shrink-0 items-center justify-center text-lc-text-tertiary hover:bg-lc-v3-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
          >
            <ToggleIcon className="size-6" aria-hidden />
          </button>
        </div>
      )}
      <div className="w-full">
        <StepList
          title={onCollapseToggle ? '' : collapsed ? '' : resolvedTitle}
          steps={steps}
          selectedStepId={selectedStepId}
          onSelect={onSelect}
          collapsed={collapsed}
        />
      </div>
    </aside>
  )
}
